import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, AppBar, Toolbar, Typography, IconButton, Drawer, List, ListItem,
  ListItemButton, ListItemIcon, ListItemText, Divider, Avatar, Menu, MenuItem,
  Tooltip, useMediaQuery, useTheme, Badge
} from '@mui/material';
import {
  Menu as MenuIcon, Dashboard as DashboardIcon, People, Timeline,
  PhoneInTalk, CheckCircleOutlined, Logout, CalendarMonth, Notifications
} from '@mui/icons-material';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from './firebase';
import { useAuth } from './AuthContext';
import NotificheDrawer from './NotificheDrawer';

const NAV = [
  { path: '/',           label: 'Dashboard', icon: <DashboardIcon /> },
  { path: '/contatti',   label: 'Contatti',  icon: <People /> },
  { path: '/pipeline',   label: 'Pipeline',  icon: <Timeline /> },
  { path: '/attivita',   label: 'Attività',  icon: <PhoneInTalk /> },
  { path: '/calendario', label: 'Calendario',icon: <CalendarMonth /> },
  { path: '/task',       label: 'Task',      icon: <CheckCircleOutlined /> }
];

const DRAWER_WIDTH    = 220;
const DRAWER_COLLAPSED = 64;

export default function AppShell({ children }) {
  const { staff, logout } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const theme     = useTheme();
  const isMobile  = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen]         = useState(false);
  const [collapsed, setCollapsed]           = useState(false);
  const [anchor, setAnchor]                 = useState(null);
  const [notifOpen, setNotifOpen]           = useState(false);
  const [unreadCount, setUnreadCount]       = useState(0);

  // Conteggio notifiche non lette (subscription sempre attiva)
useEffect(() => {
  if (!staff?.nome) return;
  const q = query(
    collection(db, 'crm_notifiche'),
    where('owner', '==', staff.nome),
    where('letta', '==', false)
  );
  const unsub = onSnapshot(q, s => setUnreadCount(s.size));
  return unsub;
}, [staff]);


  const desktopWidth = collapsed ? DRAWER_COLLAPSED : DRAWER_WIDTH;
  const showLabel    = isMobile || !collapsed;

  const handleMenuClick = () => {
    if (isMobile) setMobileOpen(true);
    else setCollapsed(c => !c);
  };

  const drawer = (
    <Box sx={{ overflowX: 'hidden' }}>
      <Toolbar>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 32, height: 32, bgcolor: 'primary.main', color: 'white', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, flexShrink: 0 }}>A</Box>
          {showLabel && (
            <Box>
              <Typography variant="subtitle2" sx={{ lineHeight: 1.2 }}>AUCTORIS</Typography>
              <Typography variant="caption" color="text.secondary">HUB</Typography>
            </Box>
          )}
        </Box>
      </Toolbar>
      <Divider />
      <List>
        {NAV.map(item => {
          const btn = (
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => { navigate(item.path); setMobileOpen(false); }}
              sx={{ justifyContent: showLabel ? 'initial' : 'center', px: 2.5, minHeight: 48 }}
            >
              <ListItemIcon sx={{ minWidth: 0, mr: showLabel ? 2 : 'auto', justifyContent: 'center' }}>
                {item.icon}
              </ListItemIcon>
              {showLabel && <ListItemText primary={item.label} />}
            </ListItemButton>
          );
          return (
            <ListItem key={item.path} disablePadding sx={{ display: 'block' }}>
              {showLabel ? btn : <Tooltip title={item.label} placement="right">{btn}</Tooltip>}
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar position="fixed" color="inherit" elevation={0}
        sx={{ borderBottom: 1, borderColor: 'divider', zIndex: t => t.zIndex.drawer + 1 }}
      >
        <Toolbar>
          <IconButton edge="start" onClick={handleMenuClick} sx={{ mr: 2 }}>
            <MenuIcon />
          </IconButton>

          <Typography variant="h6" sx={{ flexGrow: 1, fontSize: 16 }}>
            {NAV.find(n => n.path === location.pathname)?.label || 'HUB'}
          </Typography>

          {/* Bell notifiche */}
          <Tooltip title="Notifiche">
            <IconButton onClick={() => setNotifOpen(true)} sx={{ mr: 0.5 }}>
              <Badge badgeContent={unreadCount} color="error" max={99}>
                <Notifications />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* Avatar + menu utente */}
          <IconButton onClick={e => setAnchor(e.currentTarget)}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 14 }}>
              {(staff?.nome || 'S').charAt(0)}
            </Avatar>
          </IconButton>
          <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
            <MenuItem disabled>{staff?.nome} · {staff?.role}</MenuItem>
            <Divider />
            <MenuItem onClick={() => { logout(); setAnchor(null); }}>
              <Logout fontSize="small" sx={{ mr: 1 }} /> Esci
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      {isMobile ? (
        <Drawer open={mobileOpen} onClose={() => setMobileOpen(false)}
          sx={{ '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' } }}
        >
          {drawer}
        </Drawer>
      ) : (
        <Drawer variant="permanent" sx={{
          width: desktopWidth, flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: desktopWidth, boxSizing: 'border-box',
            overflowX: 'hidden',
            transition: theme.transitions.create('width', { duration: 200 })
          }
        }}>
          {drawer}
        </Drawer>
      )}

      {/* Main content */}
      <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, md: 3 }, mt: 8, minWidth: 0 }}>
        {children}
      </Box>

      {/* Drawer notifiche */}
      <NotificheDrawer open={notifOpen} onClose={() => setNotifOpen(false)} />
    </Box>
  );
}